// Product details (spec §5): what the product is, what it costs, what you
// need — and the Apply call-to-action.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheck } from '@fortawesome/free-solid-svg-icons';
import { checkCibil, getProducts } from '@/api/loans';
import { ApiError } from '@/api/client';
import type { CibilResult, LoanProduct, LoanProductFee } from '@/types/loan.types';
import { formatIndianCurrency } from '@/utils/currency';
import Loader from '@/components/ui/Loader/Loader';
import Button from '@/components/ui/Button/Button';
import { ErrorLine } from './loanShared';

const feeLabel = (fee: LoanProductFee): string => {
  if (fee.kind === 'NONE') return 'None';
  if (fee.kind === 'FLAT') return formatIndianCurrency(fee.flatRupees ?? 0);
  return `${fee.pct}% of the loan amount${fee.maxRupees ? `, up to ${formatIndianCurrency(fee.maxRupees)}` : ''}`;
};

const ProductDetails = () => {
  const { productId = '' } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<LoanProduct | null>(null);
  const [consent, setConsent] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cibil, setCibil] = useState<CibilResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getProducts()
      .then((all) => setProduct(all.find((p) => p.id === productId) ?? null))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load the product.'));
  }, [productId]);

  const onCheck = async () => {
    if (!product) return;
    setChecking(true);
    setError('');
    try {
      setCibil(await checkCibil(product.id, consent));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The check failed.');
    } finally {
      setChecking(false);
    }
  };

  if (!product) {
    return (
      <div className="nova-loans-page">
        {error ? <ErrorLine message={error} /> : <Loader rows={3} />}
      </div>
    );
  }

  const range = `${formatIndianCurrency(product.minRupees)} – ${formatIndianCurrency(product.maxRupees)}`;

  return (
    <div className="nova-loans-page">
      <div className="nova-loans-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/borrow')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>{product.name}</h2>
      </div>

      <p className="nova-loan-tagline">{product.tagline}</p>

      <div className="nova-loan-facts">
        <div className="nova-loan-fact">
          <span>Amount</span>
          <strong>{range}</strong>
        </div>
        {product.tenureMinMonths && (
          <div className="nova-loan-fact">
            <span>Tenure</span>
            <strong>
              {product.tenureMinMonths}–{product.tenureMaxMonths} months
            </strong>
          </div>
        )}
        <div className="nova-loan-fact">
          <span>Processing fee</span>
          <strong>{feeLabel(product.fee)}</strong>
        </div>
        <div className="nova-loan-fact">
          <span>Missed-EMI charge</span>
          <strong>{formatIndianCurrency(product.missedEmiChargeRupees)}</strong>
        </div>
      </div>

      <h3 className="nova-loan-section">What you need</h3>
      <ul className="nova-loan-docs">
        {product.documents.map((d) => (
          <li key={d}>
            <FontAwesomeIcon icon={faCheck} /> {d}
          </li>
        ))}
      </ul>

      <h3 className="nova-loan-section">Check your eligibility</h3>
      <label className="nova-loan-consent">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>I agree to NovaBank fetching my credit score for this check. This does not affect my score.</span>
      </label>
      <Button onClick={onCheck} disabled={!consent} isLoading={checking}>
        Check eligibility
      </Button>

      {cibil && (
        <div className={`nova-loan-cibil ${cibil.eligible ? 'is-ok' : 'is-bad'}`}>
          <div className="nova-loan-cibil-score">
            <span>Your credit score</span>
            <strong>{cibil.score}</strong>
          </div>
          {cibil.eligible ? (
            <>
              <p>
                You are eligible{product.id === 'PERSONAL' ? '' : ' to apply'}.{' '}
                {cibil.ratePaPct !== undefined && `Indicative rate: ${cibil.ratePaPct.toFixed(2)}% p.a.`}
              </p>
              <Button onClick={() => navigate(`/borrow/apply/${product.id}`)}>Apply now</Button>
            </>
          ) : (
            <p>{cibil.message ?? 'This loan is not available right now.'}</p>
          )}
        </div>
      )}
      <ErrorLine message={error} />
    </div>
  );
};

export default ProductDetails;
